import React, { createContext, useState } from "react";

import { delay } from "utils/polling";

enum SlideDirection {
  down = "down",
  up = "up",
  right = "right",
  left = "left",
}

type SlideAnimationProps = {
  direction: SlideDirection;
  in: boolean;
};

interface SlideAnimationContextInterface {
  slideOutUp: () => Promise<void>;
  slideInUp: () => Promise<void>;
  slideInDown: () => Promise<void>;
  slideOutDown: () => Promise<void>;
  slideOutLeft: () => Promise<void>;
  slideInLeft: () => Promise<void>;
  slideInRight: () => Promise<void>;
  slideOutRight: () => Promise<void>;
  slideProps: SlideAnimationProps;
}

const defaultContextMissingFunction = () => {
  throw new Error("context is missing");
};

const defaultInterface = {
  slideOutUp: defaultContextMissingFunction,
  slideInUp: defaultContextMissingFunction,
  slideInDown: defaultContextMissingFunction,
  slideOutDown: defaultContextMissingFunction,
  slideOutLeft: defaultContextMissingFunction,
  slideInLeft: defaultContextMissingFunction,
  slideInRight: defaultContextMissingFunction,
  slideOutRight: defaultContextMissingFunction,
  slideProps: {
    direction: SlideDirection.up,
    in: false,
  },
};

const SlideAnimationContext =
  createContext<SlideAnimationContextInterface>(defaultInterface);

interface SlideAnimationProviderProps {
  children: React.ReactElement;
  defaultDirection?: SlideDirection;
}

export const ENTER_DELAY = 300;
export const EXIT_DELAY = 150;
export const DELAY_BUFFER = 80;

const SlideAnimationProvider = ({ children }: SlideAnimationProviderProps) => {
  const [slideProps, setSlideProps] = useState<SlideAnimationProps>({
    direction: SlideDirection.up,
    in: false,
  });

  const slideOutUp = async () => {
    setSlideProps({
      direction: SlideDirection.down,
      in: false,
    });
    return await delay(EXIT_DELAY + DELAY_BUFFER);
  };

  const slideInUp = async () => {
    setSlideProps({
      direction: SlideDirection.up,
      in: true,
    });
    return await delay(ENTER_DELAY + DELAY_BUFFER);
  };

  const slideInDown = async () => {
    setSlideProps({
      direction: SlideDirection.down,
      in: true,
    });
    return await delay(ENTER_DELAY + DELAY_BUFFER);
  };

  const slideOutDown = async () => {
    setSlideProps({
      direction: SlideDirection.up,
      in: false,
    });
    return await delay(EXIT_DELAY + DELAY_BUFFER);
  };

  const slideOutLeft = async () => {
    setSlideProps({
      direction: SlideDirection.right,
      in: false,
    });
    return await delay(EXIT_DELAY + DELAY_BUFFER);
  };

  const slideInLeft = async () => {
    setSlideProps({
      direction: SlideDirection.left,
      in: true,
    });
    return await delay(ENTER_DELAY + DELAY_BUFFER);
  };

  const slideInRight = async () => {
    setSlideProps({
      direction: SlideDirection.right,
      in: true,
    });
    return await delay(ENTER_DELAY + DELAY_BUFFER);
  };

  const slideOutRight = async () => {
    setSlideProps({
      direction: SlideDirection.left,
      in: false,
    });
    return await delay(EXIT_DELAY + DELAY_BUFFER);
  };

  return (
    <SlideAnimationContext.Provider
      value={{
        slideOutUp,
        slideInUp,
        slideInDown,
        slideOutDown,
        slideInLeft,
        slideOutLeft,
        slideInRight,
        slideOutRight,
        slideProps,
      }}
    >
      {children}
    </SlideAnimationContext.Provider>
  );
};

export { SlideAnimationProvider, SlideAnimationContext };
